// The Check Tournament report.
//
// The checks and their wording live in js/utils/preflight.js; this file runs them against
// whatever is open and lays the findings out. It deliberately blocks nothing — see that
// module's header for why almost every finding here is legitimate at some point during a
// running event, and why the useful moment for all of them is just before the tournament
// goes to somebody else.

import { state, getActiveSettings, konradSettingsFrom } from '../state.js';
import { escapeHtml } from '../utils/formatting.js';
import { checkTournament } from '../utils/preflight.js';

const LEVEL_LOOK = {
    error: { icon: 'ri-close-circle-line', colour: 'var(--danger)', label: 'Must be fixed' },
    warning: { icon: 'ri-error-warning-line', colour: 'var(--warning)', label: 'Worth checking' },
    info: { icon: 'ri-information-line', colour: 'var(--primary)', label: 'Worth knowing' }
};

const modal = () => document.getElementById('tournament-check-modal');

// The tournaments whose check the arbiter has asked for in this session. Only those get
// a badge: an in-house club night that will never be rated always has "something wrong"
// (no FIDE IDs, no federation), and a count saying so on every visit is noise. Once the
// arbiter has run it, the badge stays and follows the fixes as they are made.
const checkedTournaments = new Set();
const checkKey = () => state.activeTournament?.id;

/** Run the checks against the active tournament and its active group. */
export function runCheck() {
    const settings = getActiveSettings();
    return checkTournament({
        players: state.players,
        rounds: state.rounds,
        settings,
        tournament: state.activeTournament || {},
        // Konrad lets a pair meet more than once on purpose, so what counts as a repeat
        // is the system's own allowance rather than "more than one".
        maxMeetings: settings.format === 'konrad'
            ? konradSettingsFrom(settings.konradSettings).maxMeetings
            : 1
    });
}

function findingHtml(f) {
    const look = LEVEL_LOOK[f.level] || LEVEL_LOOK.info;
    return `
        <div style="display: flex; gap: 0.75rem; align-items: flex-start; padding: 0.75rem 0; border-bottom: 1px solid var(--border-color);">
            <i class="${look.icon}" style="color: ${look.colour}; font-size: 1.15rem; margin-top: 0.1rem; flex-shrink: 0;"></i>
            <div style="min-width: 0;">
                <div style="word-break: break-word;">${escapeHtml(tr(f.message.key, f.message.params))}</div>
                <div style="color: var(--text-muted); font-size: 0.75rem; margin-top: 0.15rem;">${escapeHtml(tr(look.label))}</div>
            </div>
        </div>`;
}

function summaryText(counts) {
    if (counts.error) {
        return tr('{errors} things must be fixed, and {warnings} are worth checking.',
            { errors: counts.error, warnings: counts.warning });
    }
    if (counts.warning) {
        return tr('Nothing has to be fixed, but {warnings} things are worth checking.',
            { warnings: counts.warning });
    }
    return tr('Nothing to fix and nothing to check.');
}

export function openTournamentCheck() {
    if (!state.activeTournament) return;

    checkedTournaments.add(checkKey());
    syncCheckBadge();

    const { findings, counts } = runCheck();
    const summary = document.getElementById('tournament-check-summary');
    const list = document.getElementById('tournament-check-list');

    if (summary) summary.textContent = summaryText(counts);
    if (list) {
        list.innerHTML = findings.length
            ? findings.map(findingHtml).join('')
            : `<div style="text-align: center; padding: 2rem; color: var(--text-muted);">
                   <i class="ri-checkbox-circle-line" style="font-size: 2.5rem; color: var(--success); display: block; margin-bottom: 0.75rem;"></i>
                   ${escapeHtml(tr('This tournament is ready to export.'))}
               </div>`;
    }

    const m = modal();
    if (m) m.classList.add('active');
}

/**
 * A count on the Check Tournament button, once the arbiter has run the check for this
 * tournament (see checkedTournaments). Errors win over warnings; a clean tournament gets
 * no badge rather than a green zero, which would be one more thing on screen saying nothing.
 */
export function syncCheckBadge() {
    const button = document.getElementById('btn-check-tournament');
    if (!button) return;

    if (!state.activeTournament || state.isSpectatorMode) {
        button.style.display = 'none';
        return;
    }
    button.style.display = '';

    let badge = button.querySelector('.check-badge');
    if (!checkedTournaments.has(checkKey())) {
        if (badge) badge.remove();
        return;
    }
    const { counts } = runCheck();
    const count = counts.error || counts.warning || 0;

    if (!count) {
        if (badge) badge.remove();
        return;
    }
    if (!badge) {
        badge = document.createElement('span');
        badge.className = 'check-badge';
        badge.style.cssText = 'margin-left: 0.4rem; padding: 0.05rem 0.4rem; border-radius: 100px; font-size: 0.7rem; font-weight: 700; color: #fff;';
        button.appendChild(badge);
    }
    badge.textContent = String(count);
    badge.style.background = counts.error ? 'var(--danger)' : 'var(--warning)';
    badge.title = counts.error
        ? tr('{n} things must be fixed', { n: counts.error })
        : tr('{n} things are worth checking', { n: counts.warning });
}

export function setupTournamentCheck() {
    const button = document.getElementById('btn-check-tournament');
    if (button) button.addEventListener('click', openTournamentCheck);
    syncCheckBadge();
}
